/**
 * User Preferences Context
 * Manages user-specific settings and preferences
 * Language: ui_language (interface) + document_language (print/PDF) are independent.
 */

import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { message } from 'antd';
import { useAuthStore } from '@/store/authStore';
import userPreferencesService from '@/services/userPreferences.service';
import {
  DEFAULT_DOCUMENT_LANGUAGE,
  DEFAULT_UI_LANGUAGE,
  resolveUiLanguage,
} from '@/i18n/languageRegistry';

export interface UserPreferences {
  theme: 'light' | 'dark' | 'auto';
  primaryColor: string;
  compactMode: boolean;
  fontSize: 'small' | 'medium' | 'large';

  /** @deprecated Prefer ui_language — kept for backward compatibility */
  language: string;
  /** Interface / chrome language (drives i18n + RTL) */
  ui_language: string;
  /** Print / PDF / outbound document language — independent of UI */
  document_language: string;
  dateFormat: string;
  timeFormat: '12h' | '24h';
  timezone: string;

  defaultDashboard: string;
  dashboardWidgets: string[];

  sidebarCollapsed: boolean;
  showBreadcrumbs: boolean;
  showQuickActions: boolean;

  defaultWarehouse: string | null;
  defaultCurrency: string | null;
  itemsPerPage: number;

  enableNotifications: boolean;
  enableSounds: boolean;
  enableKeyboardShortcuts: boolean;

  developerMode: boolean;
  showHelpTooltips: boolean;
}

const defaultPreferences: UserPreferences = {
  theme: 'light',
  primaryColor: '#145A54',
  compactMode: false,
  fontSize: 'medium',

  language: DEFAULT_UI_LANGUAGE,
  ui_language: DEFAULT_UI_LANGUAGE,
  document_language: DEFAULT_DOCUMENT_LANGUAGE,
  dateFormat: 'YYYY-MM-DD',
  timeFormat: '24h',
  timezone: 'UTC',

  defaultDashboard: 'overview',
  dashboardWidgets: [],

  sidebarCollapsed: false,
  showBreadcrumbs: true,
  showQuickActions: true,

  defaultWarehouse: null,
  defaultCurrency: null,
  itemsPerPage: 10,

  enableNotifications: true,
  enableSounds: true,
  enableKeyboardShortcuts: true,

  developerMode: false,
  showHelpTooltips: true,
};

function normalizePrefs(raw: Partial<UserPreferences> | Record<string, unknown> | null | undefined): UserPreferences {
  const merged = { ...defaultPreferences, ...(raw || {}) } as UserPreferences;
  const resolvedUi = resolveUiLanguage({
    userLanguage: (merged.ui_language || merged.language) as string,
    allowBrowserSeed: false,
  });
  merged.ui_language = resolvedUi;
  merged.language = resolvedUi;
  if (!merged.document_language) {
    merged.document_language = DEFAULT_DOCUMENT_LANGUAGE;
  }
  return merged;
}

interface UserPreferencesContextType {
  preferences: UserPreferences;
  updatePreference: <K extends keyof UserPreferences>(key: K, value: UserPreferences[K]) => Promise<void>;
  updatePreferences: (updates: Partial<UserPreferences>) => Promise<void>;
  resetPreferences: () => Promise<void>;
  loading: boolean;
}

const UserPreferencesContext = createContext<UserPreferencesContextType | undefined>(undefined);

export const useUserPreferences = () => {
  const context = useContext(UserPreferencesContext);
  if (!context) {
    throw new Error('useUserPreferences must be used within UserPreferencesProvider');
  }
  return context;
};

interface UserPreferencesProviderProps {
  children: ReactNode;
}

const STORAGE_KEY = 'userPreferences';

export const UserPreferencesProvider: React.FC<UserPreferencesProviderProps> = ({ children }) => {
  const [preferences, setPreferences] = useState<UserPreferences>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) return normalizePrefs(JSON.parse(stored));
    } catch {
      /* ignore */
    }
    // First visit with no stored prefs: optionally seed from browser once
    const seeded = resolveUiLanguage({
      allowBrowserSeed: true,
      browserLanguage: typeof navigator !== 'undefined' ? navigator.language : null,
    });
    return normalizePrefs({ ui_language: seeded, language: seeded });
  });
  const [loading, setLoading] = useState(true);
  const userId = useAuthStore((s) => s.user?.id);
  const hydratedFromServer = useRef(false);

  useEffect(() => {
    applyTheme(preferences.theme);
    applyFontSize(preferences.fontSize);
    applyPrimaryColor(preferences.primaryColor);
  }, [preferences.theme, preferences.fontSize, preferences.primaryColor]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!userId) {
          hydratedFromServer.current = false;
          setLoading(false);
          return;
        }
        const remote = await userPreferencesService.getMine();
        if (cancelled) return;
        if (remote && Object.keys(remote).length) {
          setPreferences((prev) => {
            const next = normalizePrefs({ ...prev, ...remote });
            localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
            return next;
          });
          hydratedFromServer.current = true;
        }
      } catch {
        /* offline / unauthorized — keep localStorage */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- hydrate once per login user
  }, [userId]);

  const persist = async (updated: UserPreferences, quiet = false) => {
    setPreferences(updated);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    if (userId) {
      try {
        await userPreferencesService.updateMine(updated as unknown as Record<string, unknown>);
      } catch (error) {
        console.error('Failed to sync preferences to server:', error);
        if (!quiet) message.warning('Saved locally; server sync failed');
        return;
      }
    }
    if (!quiet) message.success('Preferences updated');
  };

  const updatePreference = async <K extends keyof UserPreferences>(key: K, value: UserPreferences[K]) => {
    const patch: Partial<UserPreferences> = { [key]: value } as Partial<UserPreferences>;
    if (key === 'ui_language' || key === 'language') {
      const code = resolveUiLanguage({ userLanguage: String(value) });
      patch.ui_language = code;
      patch.language = code;
    }
    const updated = normalizePrefs({ ...preferences, ...patch });
    const quiet = key === 'ui_language' || key === 'language' || key === 'document_language';
    await persist(updated, quiet);
    if (quiet && (key === 'ui_language' || key === 'language')) {
      message.success('Language updated');
    }
  };

  const updatePreferences = async (updates: Partial<UserPreferences>) => {
    const updated = normalizePrefs({ ...preferences, ...updates });
    await persist(updated);
  };

  const resetPreferences = async () => {
    await persist(defaultPreferences, true);
    message.success('Preferences reset to defaults');
  };

  const applyTheme = (theme: 'light' | 'dark' | 'auto') => {
    const root = document.documentElement;
    let effectiveTheme = theme;
    if (theme === 'auto') {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      effectiveTheme = prefersDark ? 'dark' : 'light';
    }
    if (effectiveTheme === 'dark') {
      root.setAttribute('data-theme', 'dark');
      document.body.style.backgroundColor = '#141414';
      document.body.style.color = '#ffffff';
    } else {
      root.setAttribute('data-theme', 'light');
      document.body.style.backgroundColor = '#f0f2f5';
      document.body.style.color = '#000000';
    }
  };

  const applyFontSize = (fontSize: 'small' | 'medium' | 'large') => {
    const sizes = { small: '12px', medium: '14px', large: '16px' };
    document.documentElement.style.fontSize = sizes[fontSize];
  };

  const applyPrimaryColor = (color: string) => {
    document.documentElement.style.setProperty('--primary-color', color);
  };

  const value: UserPreferencesContextType = {
    preferences,
    updatePreference,
    updatePreferences,
    resetPreferences,
    loading,
  };

  return (
    <UserPreferencesContext.Provider value={value}>{children}</UserPreferencesContext.Provider>
  );
};

export default UserPreferencesProvider;
